import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { createOrder } from "../../api/orders";
import type { OrderCreateInput, OrderCreateSummary } from "../../api/orders";
import { formError } from "../../lib/formError";
import type { FormError } from "../../lib/formError";
import { cartQueryKey } from "../cart/useCart";

export interface CreateOrder {
  submit: (input: OrderCreateInput) => void;
  isPending: boolean;
  error: FormError;
  data: OrderCreateSummary | undefined;
}

/**
 * useCreateOrder оформляет заказ из текущей корзины (docs/07-miniapp-ux.md §2.5).
 * Idempotency-Key генерируется один раз на время жизни хука и переживает
 * повторные вызовы submit: повтор после сетевой ошибки возвращает уже
 * созданный заказ, а не создаёт второй (docs/04-api-contract.md §3). Уход
 * с экрана оформления и возврат пересоздаёт компонент — это уже новая
 * попытка с новым ключом.
 */
export function useCreateOrder(): CreateOrder {
  const queryClient = useQueryClient();
  const idempotencyKey = useRef(crypto.randomUUID());

  const mutation = useMutation({
    mutationFn: (input: OrderCreateInput) => createOrder(input, idempotencyKey.current),
    onSuccess: () => {
      // Заказ создан из корзины — она теперь пуста на сервере.
      void queryClient.invalidateQueries({ queryKey: cartQueryKey });
    },
  });

  return {
    submit: (input) => mutation.mutate(input),
    isPending: mutation.isPending,
    error: formError(mutation.error),
    data: mutation.data,
  };
}
